import { DefaultValues } from "react-hook-form";
import * as yup from "yup";
import { TransferStatus, UnitOfMeasure } from "../../../../common/enums";
import { getInventoryItemDisplayName } from "../../../../common/utilities/inventoryDisplayUtility";

/* ================================================================
 * View models (shape returned by GetAll / GetById)
 * ================================================================ */

export interface IInventoryTransferListItemModel {
  id: number;
  transferNumber: number;
  displayNumber: string; // "#D-0001"
  deliveryDate: string;
  completedOn: string | null;
  status: TransferStatus;
  supplier: string | null;
  createdById: number;
  createdByName: string | null;
  createdOn: string;
  modifiedOn: string | null;
  isDeleted: boolean;
  notesCount: number;
  lines: IInventoryTransferLineViewModel[];
}

export interface IInventoryTransferLineViewModel {
  id: number;
  inventoryItemId: number;
  productName: string;
  categoryEmoji: string | null;
  unitOfMeasure: UnitOfMeasure;
  quantity: number;
  expirationDate: string | null;
}

/* ================================================================
 * Search result (product picker in transfer + cashdesk)
 * ================================================================ */

export interface IInventoryItemSearchResultModel {
  id: number;
  name: string;
  brand: string | null;
  emoji: string | null;
  sku: string | null;
  barcode: string | null;
  currentQuantity: number;
  unitOfMeasure: UnitOfMeasure;
  lowStockThreshold: number;
  price: number;
}

/* ================================================================
 * Form model + yup validation
 * ================================================================ */

/**
 * Represents one line in the react-hook-form `lines` field array.
 * Mirrors InventoryTransferLineInputModel on the backend, plus a few
 * denormalised fields (productName, emoji, uom) so the lines table can
 * render without looking the product up on every render.
 */
export interface IInventoryTransferLineFormModel {
  /** Null for newly added lines; populated for existing lines on edit. */
  id: number | null;
  inventoryItemId: number;
  quantity: number;
  expirationDate: Date | null;

  // Display-only denormalised fields
  productName: string;
  emoji: string | null;
  unitOfMeasure: UnitOfMeasure;
}

export interface IInventoryTransferFormModel {
  id: number | null;
  deliveryDate: Date;
  status: TransferStatus;
  supplier: string | null;
  lines: IInventoryTransferLineFormModel[];
}

export const inventoryTransferLineSchema = yup
  .object({
    id: yup.number().nullable().defined(),
    inventoryItemId: yup
      .number()
      .required("Продуктът е задължителен")
      .min(1, "Продуктът е задължителен")
      .defined(),
    quantity: yup
      .number()
      .typeError("Количеството е задължително")
      .positive("Количеството трябва да е положително число")
      .integer("Количеството трябва да е цяло число")
      .required("Количеството е задължително")
      .defined(),
    expirationDate: yup.date().nullable().defined(),
    productName: yup.string().defined(),
    emoji: yup.string().nullable().defined(),
    unitOfMeasure: yup
      .number()
      .oneOf([UnitOfMeasure.Piece, UnitOfMeasure.Gram])
      .defined(),
  })
  .required();

export const inventoryTransferSchema = yup
  .object({
    id: yup.number().nullable().defined(),
    deliveryDate: yup
      .date()
      .typeError("Датата на доставка е задължителна")
      .required("Датата на доставка е задължителна")
      .defined(),
    status: yup
      .number()
      .oneOf([TransferStatus.Planned, TransferStatus.Completed])
      .required()
      .defined(),
    supplier: yup.string().nullable().defined(),
    lines: yup
      .array()
      .of(inventoryTransferLineSchema)
      .min(1, "Доставката трябва да съдържа поне един продукт")
      .required()
      .defined(),
  })
  .required();

export const getInventoryTransferDefaults = (): DefaultValues<IInventoryTransferFormModel> => ({
  id: null,
  deliveryDate: new Date(),
  status: TransferStatus.Planned,
  supplier: "",
  lines: [],
});

/**
 * Converts a backend view model into a form model. Used when the dialog
 * opens in edit mode.
 */
export const viewModelToFormModel = (
  transfer: IInventoryTransferListItemModel
): IInventoryTransferFormModel => ({
  id: transfer.id,
  deliveryDate: new Date(transfer.deliveryDate),
  status: transfer.status,
  supplier: transfer.supplier ?? "",
  lines: transfer.lines.map((line) => ({
    id: line.id,
    inventoryItemId: line.inventoryItemId,
    quantity: line.quantity,
    expirationDate: line.expirationDate ? new Date(line.expirationDate) : null,
    productName: line.productName,
    emoji: line.categoryEmoji,
    unitOfMeasure: line.unitOfMeasure,
  })),
});

/**
 * Converts a search result into a fresh form line. Quantity defaults to 1.
 */
export const searchResultToFormLine = (
  product: IInventoryItemSearchResultModel
): IInventoryTransferLineFormModel => ({
  id: null,
  inventoryItemId: product.id,
  quantity: 1,
  expirationDate: null,
  productName: getInventoryItemDisplayName(product.name, product.brand),
  emoji: product.emoji,
  unitOfMeasure: product.unitOfMeasure,
});

/**
 * Serialises a form model into the backend InventoryTransferInputModel shape.
 * Drops denormalised display fields and coerces dates to ISO strings.
 *
 * `name` is intentionally omitted — the service synthesises it server-side as
 * `#D-{TransferNumber:D4}`. The base [Unique] validator on `Name` is never
 * reached because the InventoryTransfersService fully overrides Create/Update.
 */
export const formModelToPayload = (form: IInventoryTransferFormModel) => ({
  name: "transfer",
  deliveryDate: form.deliveryDate,
  status: form.status,
  supplier: form.supplier?.trim() || null,
  lines: form.lines.map((line) => ({
    id: line.id,
    inventoryItemId: line.inventoryItemId,
    quantity: line.quantity,
    expirationDate: line.expirationDate,
  })),
});
